// Design System PageHeader Component
// Unified header component for all page types including forms

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Fade,
  useTheme,
  alpha,
  styled,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

// Import design tokens
import { spacingTokens, colorUtils } from '../../tokens';

// Styled components using design tokens
const HeaderContainer = styled(Box)(({ theme, hasBottomDivider, compact, variant }) => {
  const mode = theme.palette.mode;
  
  const baseStyles = {
    position: 'relative',
    transition: 'all 0.3s ease',
  };
  
  // Variant styles
  const variantStyles = {
    default: {
      padding: `${spacingTokens.layout.md}px 0 ${hasBottomDivider ? spacingTokens.layout.lg : spacingTokens.layout.md}px 0`,
      marginBottom: spacingTokens.layout.lg,
    },
    compact: {
      padding: `${spacingTokens.layout.sm}px 0 ${hasBottomDivider ? spacingTokens.layout.md : spacingTokens.layout.sm}px 0`,
      marginBottom: spacingTokens.layout.md,
    },
    form: {
      padding: `${spacingTokens.layout.md}px 0`,
      marginBottom: spacingTokens.layout.md,
    },
  };
  
  // Bottom divider
  const dividerStyles = hasBottomDivider ? {
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 1,
      background: `linear-gradient(90deg, 
        ${alpha(theme.palette.divider, 0)} 0%, 
        ${alpha(theme.palette.divider, 0.7)} 50%, 
        ${alpha(theme.palette.divider, 0)} 100%)`,
    }
  } : {};
  
  return {
    ...baseStyles,
    ...variantStyles[variant || (compact ? 'compact' : 'default')],
    ...dividerStyles,
  };
});

const IconWrapper = styled(Box)(({ theme, size = 'medium' }) => {
  const mode = theme.palette.mode;
  const sizes = {
    small: spacingTokens.component.xl,
    medium: spacingTokens.component.xxl,
    large: spacingTokens.component.xxxl,
  };
  
  const sizeValue = sizes[size] || sizes.medium;
  
  return {
    width: sizeValue,
    height: sizeValue,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacingTokens.layout.sm,
    background: mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.1)
      : alpha(theme.palette.primary.light, 0.1),
    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
    color: theme.palette.primary.main,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: `0 6px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
  };
});

const StyledButton = styled(Button)(({ theme, magical }) => {
  const baseStyles = {
    borderRadius: spacingTokens.component.md,
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  };
  
  const magicalStyles = magical ? {
    boxShadow: theme.shadows[4],
    background: theme.palette.gradients?.primaryButton ||
      `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    '&:hover': {
      boxShadow: theme.shadows[8],
      transform: 'translateY(-1px)',
    },
  } : {};
  
  return {
    ...baseStyles,
    ...magicalStyles,
  };
});

/**
 * PageHeader - Unified header component for all page types
 * 
 * Features:
 * - Support for regular pages and form pages
 * - Breadcrumb navigation
 * - Flexible action system
 * - Design system consistency
 * - Magical effects support
 * - Responsive design
 */
const PageHeader = ({
  // Basic content
  title,
  subtitle,
  description,
  
  // Visuals
  icon,
  iconSize = 'medium',
  titleProps = {},
  gradientTitle = false,
  
  // Breadcrumbs
  breadcrumbs = [],
  disableBreadcrumbs = false,
  homePageLabel = 'Home',
  homePath = '/dashboard',
  
  // Navigation
  backTo,
  backLabel = 'Back',
  backUrl, // FormPageHeader compatibility
  onBack, // FormPageHeader compatibility
  onCancel, // FormPageHeader compatibility
  
  // Form specific props
  entityType,
  isEditMode = false,
  onSave,
  saving = false,
  showSaveButton = true,
  children, // For form content
  wrapContent = false, // Whether to wrap content in Paper
  
  // Actions
  actions = [],
  primaryAction,
  menuActions = [],
  
  // Styling
  hasBottomDivider = true,
  titleVariant = 'h4',
  compact = false,
  magical = false,
  variant, // 'default', 'compact', 'form'
  
  // Other props
  className,
  sx = {},
  ...rest
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Handle back navigation
  const handleBack = (e) => {
    if (e) e.preventDefault();
    
    if (onBack) {
      onBack();
    } else if (onCancel) {
      onCancel();
    } else if (backUrl) {
      navigate(backUrl);
    } else if (backTo) {
      navigate(backTo);
    }
  };
  
  // Default subtitle for forms
  const getDefaultSubtitle = () => {
    if (subtitle) return subtitle;
    
    if (entityType) {
      return isEditMode 
        ? `Update ${entityType} information and settings` 
        : `Create a new ${entityType} in the system`;
    }
    
    return subtitle;
  };
  
  // Render breadcrumbs
  const renderBreadcrumbs = () => {
    if (disableBreadcrumbs) return null;
    
    const crumbs = breadcrumbs || [];
    if (crumbs.length === 0 && !backTo && !backUrl) return null;
    
    return (
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: spacingTokens.component.sm }}
      >
        {/* Home crumb */}
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to={homePath}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontSize: '0.85rem',
            transition: 'color 0.2s ease',
            '&:hover': {
              color: 'primary.main',
            },
          }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
          {homePageLabel}
        </Link>
        
        {/* Additional breadcrumbs */}
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          
          return isLast ? (
            <Typography 
              key={index} 
              color="text.primary" 
              sx={{ 
                fontSize: '0.85rem',
                fontWeight: 500,
              }}
            >
              {crumb.label}
            </Typography>
          ) : (
            <Link
              key={index}
              underline="hover"
              color="inherit"
              component={RouterLink}
              to={crumb.path}
              sx={{ 
                fontSize: '0.85rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };
  
  // Render primary action
  const renderPrimaryAction = () => {
    if (!primaryAction) return null;
    
    const {
      label = 'Create',
      icon = <AddIcon />,
      onClick,
      linkTo,
      variant = 'contained',
      color = 'primary',
      magical: actionMagical = magical,
      ...actionProps
    } = primaryAction;
    
    return (
      <StyledButton
        variant={variant}
        color={color}
        startIcon={icon}
        onClick={onClick}
        component={linkTo ? RouterLink : undefined}
        to={linkTo}
        magical={actionMagical}
        {...actionProps}
      >
        {label}
      </StyledButton>
    );
  };
  
  // Render save button for forms
  const renderSaveButton = () => {
    if (!onSave || !showSaveButton) return null;
    
    return (
      <StyledButton
        variant="contained"
        color="primary"
        onClick={onSave}
        disabled={saving}
        endIcon={<SaveIcon />}
        magical={magical}
      >
        {saving ? 'Saving...' : (isEditMode ? `Update ${entityType || 'Item'}` : `Create ${entityType || 'Item'}`)}
      </StyledButton>
    );
  };
  
  // Handle menu actions
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleMenuItemClick = (action) => {
    handleMenuClose();
    if (action.onClick) {
      action.onClick();
    }
  };
  
  // Render actions menu
  const renderActionsMenu = () => {
    if (!menuActions || menuActions.length === 0) return null;
    
    return (
      <>
        <Tooltip title="More actions">
          <IconButton 
            color="primary" 
            size="small"
            onClick={handleMenuOpen}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: spacingTokens.component.sm,
            }}
          >
            <MoreIcon />
          </IconButton>
        </Tooltip>
        
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {menuActions.map((action, index) => (
            <MenuItem 
              key={index}
              onClick={() => handleMenuItemClick(action)}
              sx={{
                color: action.color === 'error' ? 'error.main' : undefined,
              }}
            >
              {action.icon && (
                <ListItemIcon
                  sx={{
                    color: action.color === 'error' ? 'error.main' : undefined,
                  }}
                >
                  {action.icon}
                </ListItemIcon>
              )}
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };
  
  // Determine layout mode
  const isFormLayout = Boolean(children && wrapContent);
  const actualSubtitle = getDefaultSubtitle();
  const effectiveBackTo = backTo || backUrl;
  const headerVariant = variant || (isFormLayout ? 'form' : (compact ? 'compact' : 'default'));
  
  // Create header content
  const headerContent = (
    <HeaderContainer 
      className={className}
      hasBottomDivider={isFormLayout ? false : hasBottomDivider}
      compact={compact}
      variant={headerVariant}
      sx={sx}
      {...rest}
    >
      {/* Breadcrumbs */}
      {renderBreadcrumbs()}
      
      {/* Main header content */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: spacingTokens.layout.sm,
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flexGrow: 1,
          minWidth: 0,
        }}>
          {/* Back button */}
          {effectiveBackTo && (
            <Fade in={true}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={{ 
                  mr: spacingTokens.component.md,
                  borderRadius: spacingTokens.component.sm,
                }}
                variant="text"
                color="inherit"
              >
                {backLabel}
              </Button>
            </Fade>
          )}
          
          {/* Icon */}
          {icon && (
            <IconWrapper size={iconSize}>
              {React.isValidElement(icon) ? icon : icon}
            </IconWrapper>
          )}
          
          {/* Title and subtitle */}
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              variant={titleVariant}
              component="h1"
              color="textPrimary"
              sx={{
                fontWeight: 600,
                ...(gradientTitle && {
                  background: theme.palette.gradients?.text || 
                    `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }),
                ...titleProps.sx,
              }}
              {...titleProps}
            >
              {title || (isEditMode ? `Edit ${entityType}` : `Create ${entityType}`)}
            </Typography>

            {actualSubtitle && (
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{ mt: spacingTokens.component.xs }}
              >
                {actualSubtitle}
              </Typography>
            )}
            
            {description && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ 
                  mt: spacingTokens.component.sm, 
                  maxWidth: '800px',
                  lineHeight: 1.6,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        </Box>
        
        {/* Actions area */}
        {(actions.length > 0 || primaryAction || menuActions.length > 0 || (onSave && showSaveButton)) && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: spacingTokens.component.sm,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}>
            {/* Regular action buttons */}
            {actions.map((action, index) => {
              if (React.isValidElement(action)) {
                return <React.Fragment key={index}>{action}</React.Fragment>;
              }
              
              const { 
                label, 
                icon, 
                onClick, 
                variant = 'outlined',
                color = 'primary',
                linkTo,
                magical: actionMagical = false,
                ...actionProps 
              } = action;
              
              return (
                <StyledButton
                  key={index}
                  variant={variant}
                  color={color}
                  startIcon={icon}
                  onClick={onClick}
                  component={linkTo ? RouterLink : undefined}
                  to={linkTo}
                  magical={actionMagical}
                  {...actionProps}
                >
                  {label}
                </StyledButton>
              );
            })}
            
            {/* Primary action or save button */}
            {primaryAction ? renderPrimaryAction() : renderSaveButton()}
            
            {/* Actions menu */}
            {renderActionsMenu()}
          </Box>
        )}
      </Box>
    </HeaderContainer>
  );
  
  // Form layout wrapper
  if (isFormLayout) {
    return (
      <Box sx={{ 
        py: spacingTokens.layout.lg, 
        flexGrow: 1, 
        overflow: 'auto', 
        height: '85vh',
      }}>
        {/* Page header */}
        {headerContent}
        
        {/* Form content paper */}
        <Paper 
          elevation={1} 
          sx={{ 
            p: spacingTokens.layout.lg, 
            mb: spacingTokens.layout.lg, 
            borderRadius: spacingTokens.component.lg,
            position: 'relative',
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          {children}
        </Paper>
        
        {/* Footer actions for forms */}
        {onSave && showSaveButton && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: spacingTokens.layout.lg,
            gap: spacingTokens.component.md,
          }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={saving}
              variant="outlined"
              sx={{ borderRadius: spacingTokens.component.sm }}
            >
              Cancel
            </Button>
            
            {renderSaveButton()}
          </Box>
        )}
      </Box>
    );
  }
  
  // Standard layout
  return headerContent;
};

PageHeader.propTypes = {
  // Basic content
  title: PropTypes.node,
  subtitle: PropTypes.node,
  description: PropTypes.node,
  
  // Visuals
  icon: PropTypes.node,
  iconSize: PropTypes.oneOf(['small', 'medium', 'large']),
  titleProps: PropTypes.object,
  gradientTitle: PropTypes.bool,
  
  // Breadcrumbs
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
    })
  ),
  disableBreadcrumbs: PropTypes.bool,
  homePageLabel: PropTypes.string,
  homePath: PropTypes.string,
  
  // Navigation
  backTo: PropTypes.string,
  backLabel: PropTypes.string,
  backUrl: PropTypes.string,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  
  // Form specific props
  entityType: PropTypes.string,
  isEditMode: PropTypes.bool,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
  showSaveButton: PropTypes.bool,
  children: PropTypes.node,
  wrapContent: PropTypes.bool,
  
  // Actions
  actions: PropTypes.array,
  primaryAction: PropTypes.object,
  menuActions: PropTypes.array,
  
  // Styling
  hasBottomDivider: PropTypes.bool,
  titleVariant: PropTypes.string,
  compact: PropTypes.bool,
  magical: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'compact', 'form']),
  className: PropTypes.string,
  sx: PropTypes.object,
};

export default PageHeader;